import {
  ScheduledQueryDay,
  ScheduledQueryFrequency,
  ScheduledQueryOccurrence,
  ScheduledQueryWindow,
} from '@api';
import { Button } from '@design/components/button/Button';
import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { CheckBox } from '@design/components/checkbox/CheckBox';
import { Input } from '@design/components/input/Input';
import { Toggle } from '@design/components/toggle/Toggle';
import { TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { MarkdownEditor } from '@terminal/widgets/markdown-editor/MarkdownEditor';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PromptForm.module.scss';
import {
  ScheduleOptionsRow,
  ScheduleOptionsRowProps,
  ScheduleOptionsValue,
} from './ScheduleOptionsRow';
import { isAtActiveScheduleCap } from './utils/scheduleCap';
import { WebSearchField } from './WebSearchField';

export interface PromptFormValue {
  title: string;
  query: string;
  scheduleEnabled: boolean;
  frequency: ScheduledQueryFrequency;
  day: ScheduledQueryDay;
  occurrence: ScheduledQueryOccurrence;
  deliveryTime: ScheduledQueryWindow;
  skipWeekends: boolean;
}

export const defaultPromptFormValue: PromptFormValue = {
  title: '',
  query: '',
  scheduleEnabled: false,
  frequency: 'daily',
  day: 'friday',
  occurrence: 'first',
  deliveryTime: 'morning',
  skipWeekends: false,
};

export const isPromptFormDirty = (
  current: PromptFormValue,
  original: PromptFormValue
): boolean => {
  const { title: currentTitle, query: currentQuery, ...currentRest } = current;
  const {
    title: originalTitle,
    query: originalQuery,
    ...originalRest
  } = original;

  return (
    currentTitle.trim() !== originalTitle.trim() ||
    currentQuery.trim() !== originalQuery.trim() ||
    JSON.stringify(currentRest) !== JSON.stringify(originalRest)
  );
};

export interface PromptFormProps {
  value: PromptFormValue;
  onChange: (patch: Partial<PromptFormValue>) => void;
  showScheduleToggle: boolean;
  bodyLabel: string;
  testId: string;
  showPlaceholder?: boolean;
  className?: string;
  scheduledTasksCount?: number;
  isScheduleCountPending?: boolean;
  onSubmit?: () => void;
  onCancel?: () => void;
  submitLabel?: string;
  isSubmitting?: boolean;
  isDirty?: boolean;
}

export const PromptForm = ({
  value,
  onChange,
  showScheduleToggle,
  bodyLabel,
  testId,
  showPlaceholder = true,
  className,
  scheduledTasksCount = 0,
  isScheduleCountPending = false,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting = false,
  isDirty,
}: PromptFormProps) => {
  const { t } = useTranslation(['chat']);
  const titleId = `${testId}-title`;
  const editorId = `${testId}-editor`;
  const scheduleToggleLabelId = `${testId}-schedule-toggle-label`;

  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const showCadence = showScheduleToggle ? value.scheduleEnabled : true;
  const atScheduleCap = isAtActiveScheduleCap(scheduledTasksCount);
  const scheduleDisabled =
    isScheduleCountPending || (atScheduleCap && !value.scheduleEnabled);
  const showScheduleCapTooltip = scheduleDisabled && atScheduleCap;
  const fieldRequiredLabel = t('chat:scheduledQuerySettings.fieldRequired');
  const showTitleError = showValidationErrors && value.title.trim() === '';
  const showQueryError = showValidationErrors && value.query.trim() === '';
  const showActions = onSubmit !== undefined || onCancel !== undefined;

  const handleScheduleChange: ScheduleOptionsRowProps['onChange'] = (
    field: keyof ScheduleOptionsValue,
    fieldValue:
      | ScheduledQueryFrequency
      | ScheduledQueryDay
      | ScheduledQueryOccurrence
      | ScheduledQueryWindow
  ) => {
    onChange({ [field]: fieldValue });
  };

  const handleSubmit = () => {
    if (value.title.trim() === '' || value.query.trim() === '') {
      setShowValidationErrors(true);
      return;
    }
    setShowValidationErrors(false);
    onSubmit?.();
  };

  const handleCancel = () => {
    setShowValidationErrors(false);
    onCancel?.();
  };

  const scheduleToggle = (
    <Toggle
      isChecked={value.scheduleEnabled}
      onToggle={checked => onChange({ scheduleEnabled: checked })}
      disabled={scheduleDisabled}
      aria-labelledby={scheduleToggleLabelId}
      data-testid={`${testId}-schedule-toggle`}
    />
  );

  return (
    <div
      className={clsx(
        styles.root,
        { [styles.rootWithActions]: showActions },
        className
      )}
      data-testid={testId}
    >
      <div
        className={clsx(styles.form, {
          [styles.formScrollable]: showActions,
        })}
      >
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor={titleId}>
            {t('chat:scheduledQuerySettings.title')}
          </label>
          <div className={styles.titleInputWrapper}>
            <Input
              id={titleId}
              value={value.title}
              onChange={e => onChange({ title: e.target.value })}
              placeholder={
                showPlaceholder
                  ? t('chat:scheduledQuerySettings.titlePlaceholder')
                  : undefined
              }
              variant="outline"
              hasError={showTitleError}
              data-testid={`${testId}-title`}
            />
          </div>
          {showTitleError && (
            <p
              className={styles.fieldError}
              data-testid={`${testId}-title-error`}
            >
              {fieldRequiredLabel}
            </p>
          )}
        </div>

        <div className={styles.fieldGroup}>
          <div className={styles.fieldLabelRow}>
            <label className={styles.fieldLabel} htmlFor={editorId}>
              {bodyLabel}
            </label>
            <ButtonLink
              label={t('chat:scheduledQuerySettings.showExample')}
              onClick={() =>
                onChange({
                  query: t('chat:scheduledQuerySettings.exampleQuery'),
                })
              }
              disabled={value.query.trim() !== ''}
              data-testid="show-example"
            />
          </div>
          <div
            className={clsx(styles.queryEditor, {
              [styles.queryEditorError]: showQueryError,
            })}
          >
            <MarkdownEditor
              id={editorId}
              value={value.query}
              onChange={query => onChange({ query })}
              placeholder={
                showPlaceholder
                  ? t('chat:scheduledQuerySettings.queryPlaceholder')
                  : undefined
              }
              minHeight="150px"
              dataTestId={`${testId}-editor`}
            />
          </div>
          {showQueryError && (
            <p
              className={styles.fieldError}
              data-testid={`${testId}-query-error`}
            >
              {fieldRequiredLabel}
            </p>
          )}
        </div>

        {showScheduleToggle && (
          <div className={styles.scheduleToggle}>
            <div className={styles.scheduleToggleFlex}>
              <p
                className={styles.scheduleToggleLabel}
                id={scheduleToggleLabelId}
              >
                {t('chat:promptLibrary.schedulePrompt')}
              </p>

              {showScheduleCapTooltip ? (
                <TooltipContainer
                  label={t(
                    'chat:historySidebar.scheduleTaskDisabledMaxReached'
                  )}
                  inPortal
                  trigger={[TooltipTrigger.Hover]}
                >
                  <span className={styles.scheduleToggleTooltipTarget}>
                    {scheduleToggle}
                  </span>
                </TooltipContainer>
              ) : (
                scheduleToggle
              )}
            </div>
            <span className={styles.scheduleToggleLine} aria-hidden />
          </div>
        )}

        {showCadence && (
          <>
            <WebSearchField />

            <ScheduleOptionsRow
              value={{
                frequency: value.frequency,
                day: value.day,
                occurrence: value.occurrence,
                deliveryTime: value.deliveryTime,
              }}
              onChange={handleScheduleChange}
            />

            {value.frequency === 'daily' && (
              <CheckBox
                name={`${testId}-skip-weekends`}
                label={t('chat:scheduledQuerySettings.runOnWeekends')}
                checked={!value.skipWeekends}
                onChange={(_, e) =>
                  onChange({ skipWeekends: !e.target.checked })
                }
              />
            )}
          </>
        )}
      </div>

      {showActions && (
        <footer className={styles.footer}>
          {onCancel && (
            <Button
              className={clsx(styles.footerButton, styles.footerCancelButton)}
              variant="tertiary"
              size="medium"
              label={t('chat:scheduledQuerySettings.cancel')}
              onClick={handleCancel}
              data-testid={`${testId}-cancel`}
            />
          )}
          {onSubmit && (
            <Button
              className={clsx(styles.footerButton, styles.footerSubmitButton)}
              variant="primary"
              size="medium"
              label={submitLabel ?? t('chat:scheduledQuerySettings.saveTask')}
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                showTitleError ||
                showQueryError ||
                isDirty === false
              }
              isLoading={isSubmitting}
              data-testid={`${testId}-save`}
            />
          )}
        </footer>
      )}
    </div>
  );
};
